import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { EnvelopeSimple, Trash, CheckCircle, ClockCountdown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import api from "../lib/api";

export const ContactRequests = () => {
  const { t, lang } = useLang();
  const a = t.adminReq;
  const [items, setItems] = useState(null);

  const load = () => {
    api.get("/admin/contact-requests")
      .then(({ data }) => setItems(data.items))
      .catch(() => setItems(null));
  };
  useEffect(load, []);

  if (!items) return null;

  const remove = async (id) => {
    try {
      await api.delete(`/admin/contact-requests/${id}`);
      toast.success(a.deleted);
      load();
    } catch (e) { toast.error(a.error); }
  };

  const unread = items.filter((r) => !r.read).length;

  const markAllRead = async () => {
    try {
      await api.put("/admin/contact-requests/mark-read");
      toast.success(a.marked);
      load();
    } catch (e) { toast.error(a.error); }
  };

  const fmt = (ts) => new Date(ts).toLocaleString(lang === "it" ? "it-IT" : "en-GB", { dateStyle: "medium", timeStyle: "short" });

  return (
    <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="mb-8 rounded-md border border-slate-200 bg-white p-6" data-testid="contact-requests-panel">
      <div className="flex items-center gap-2">
        <p className="font-mono-tech text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600">[ {a.title} ]</p>
        {unread > 0 && (
          <span className="rounded-full bg-emerald-500 px-2.5 py-0.5 font-mono-tech text-[10px] font-bold text-[#04170F]" data-testid="contact-requests-unread">{unread} {unread === 1 ? a.newOne : a.newMany}</span>
        )}
        {unread > 0 && (
          <button onClick={markAllRead} className="ml-auto rounded-full border border-slate-300 px-3 py-1 text-[11px] font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-700 transition-colors" data-testid="contact-requests-mark-read">{a.markRead}</button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="contact-requests-empty">{a.empty}</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-100">
          {items.map((r, i) => (
            <li key={r.id} className={`py-3.5 ${!r.read ? "border-l-2 border-l-emerald-500 pl-3 -ml-3 bg-emerald-50/40" : ""}`} data-testid={`contact-request-${i}`}>
              <div className="flex items-center gap-2">
                <EnvelopeSimple size={15} className="text-emerald-600 shrink-0" />
                <p className="text-sm font-semibold text-[#0F172A] truncate">{r.name}</p>
                {!r.read && <span className="rounded-full bg-emerald-500 px-1.5 py-px font-mono-tech text-[9px] font-bold text-[#04170F] shrink-0" data-testid={`contact-request-new-${i}`}>{a.newChip}</span>}
                <a href={`mailto:${r.email}`} className="text-xs text-emerald-700 hover:underline truncate">{r.email}</a>
                <span className="ml-auto font-mono-tech text-[11px] text-slate-400 shrink-0">{fmt(r.created_at)}</span>
                {r.email_sent ? (
                  <span title={a.sent}><CheckCircle size={15} weight="fill" className="text-emerald-500 shrink-0" /></span>
                ) : (
                  <span title={a.pending}><ClockCountdown size={15} className="text-amber-500 shrink-0" /></span>
                )}
                <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-red-500 transition-colors p-1 shrink-0" data-testid={`contact-request-delete-${i}`}><Trash size={15} /></button>
              </div>
              <p className="mt-1.5 pl-6 text-sm text-slate-600 whitespace-pre-wrap">{r.message}</p>
            </li>
          ))}
        </ul>
      )}
    </motion.section>
  );
};
